import { Component, Input, OnDestroy, ViewChild, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ModalController } from '@ionic/angular';
import { AppRoutes } from 'src/app/app.routes';
import { Order } from 'src/app/core/models/order';
import { NavigationService } from 'src/app/core/services/navigation.service';
import { OrderService } from 'src/app/core/services/order.service';
import { SmsService } from 'src/app/core/services/sms.service';
import { AccountService } from 'src/app/core/services/account.service';
import { CallService } from 'src/app/features/call/call.service';
import { OutgoinCall, CallType } from 'src/app/features/call/models/outgoin-call';
import { CourierRoutes } from 'src/app/features/courier/courier.routes';
import { OrderPreviewData } from '../../order-preview.component';
import { Subject } from 'rxjs';
import { finalize, take, takeUntil } from 'rxjs/operators';
import { EndDeliveryComponent } from '../../../end-delivery/end-delivery.component';
import { SmsMessageModalComponent } from '../../../send-sms/send-sms.component';

@Component({
  selector: 'app-order-detail',
  templateUrl: './order-detail.component.html',
  styleUrls: ['./order-detail.component.scss'],
})
export class OrderDetailComponent implements OnInit, OnDestroy {
  private readonly destroyed$: Subject<void> = new Subject();
  @ViewChild('orderListWrapper') orderListWrapper: any;
  showGradient = true;
  isStartingDelivery = false;

  @Input({ required: true }) data!: OrderPreviewData;

  constructor(
    public orderService: OrderService,
    private modalController: ModalController,
    private router: Router,
    private accountService: AccountService,
    private callService: CallService,
    private smsService: SmsService,
    private navigationService: NavigationService,
  ) { }

  ngOnInit(): void {
    this.showGradient = this.data.order.order.orderLines.length >= 5;
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  onScroll() {
    const el = this.orderListWrapper.nativeElement;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 43) {
      this.showGradient = false;
    } else {
      this.showGradient = true;
    }
  }

  async sendSms($event: any, orderId: string, phoneNumber: string, address: string): Promise<void> {
    $event.stopPropagation();

    this.accountService.getBranch()
      .pipe(take(1))
      .subscribe(async (branch) => {
        const modal = await this.modalController.create({
          component: SmsMessageModalComponent,
          componentProps: {
            orderId,
            phoneNumber,
            branchId: branch.id,
            branchName: branch.name,
            address,
          },
          cssClass: 'sms-send-modal-css',
        });

        await modal.present();
      });
  }


  callOrder($event: any, order: Order): void {
    $event.stopPropagation();
    this.accountService.getName()
      .pipe(
        take(1),
      )
      .subscribe(async (name) => {
        const outgoinCall: OutgoinCall = {
          type: CallType.WithCustomer,
          callee: {
            name: order.client.name,
            number: order.client.telephoneNumber,
          },
          caller: { name },
          order: order
        }
        this.callService.call(outgoinCall);
        const topModal = await this.modalController.getTop();
        if (topModal) {
          await this.modalController.dismiss();
        }
      })
  }

  startNavigation($event: any, order: Order): void {
    $event.stopPropagation();
    this.navigationService.startNavigation(order);
  }

  startDelivery(id: string): void {
    if (this.isStartingDelivery) return;

    this.isStartingDelivery = true;
    this.orderService.startDelivery(id).pipe(
      takeUntil(this.destroyed$),
      finalize(() => {
        this.isStartingDelivery = false;
      })
    ).subscribe();
  }

  async endDelivery(order: Order): Promise<void> {
    const modal = await this.modalController.create({
      component: EndDeliveryComponent,
      cssClass: 'end-delivery-modal-css',
      id: 'end-delivery',
      componentProps: {
        order: order
      }
    });
    await modal.present();
  }

  async passOnOrder(): Promise<void> {
    const topModal = await this.modalController.getTop();
    if (topModal) {
      await this.modalController.dismiss();
    }
    this.router.navigateByUrl(`${AppRoutes.Courier}/${CourierRoutes.PassOrder}`, { state: { order: this.data } })
  }
}
